/**
 * Digiping SMART journey state machine (pure reducer).
 * M = near-term metrics · A = mid/long milestones (+ coach) · R = why it matters (+ coach)
 */

import {
  ACTIVITY_TYPES,
  createActivityEvent,
  estimateCycleProgress,
  presenceRatioFromCyclePresence
} from './activityEvents';
import {
  applyLeisurePresetToSlots,
  isLeisurePurpose,
  isLeisureBeginnerOrIntermediate
} from '../constants/leisureSmartPresets';
import {
  applyHealthPresetToSlots,
  isHealthPurpose
} from '../constants/healthSmartPresets';
import {
  DEFAULT_HEALTH_PHASE_WEEKS,
  DEFAULT_HEALTH_SESSION_HOURS
} from '../constants/healthPlayerProfiling';
import { isLuxembourgClubCountry } from '../constants/countries';
import { isBelgiumClub } from '../utils/clubCountryLanguage';

export const DIMENSIONS = Object.freeze(['S', 'M', 'A', 'R', 'T']);

export const PHASES = Object.freeze({
  INTRO: 'intro',
  CAPTURE: 'capture',
  ASSESSMENT: 'assessment',
  ACTIVE: 'active',
  REVIEW: 'review'
});

export const emptyCoachAgreement = () => ({
  status: null, // null | 'pending' | 'approved' | 'rejected'
  covers: ['A', 'R'],
  by: null,
  at: null,
  note: '',
  revisedMilestones: null
});

/** Fine-grained wizard step → SMART dimension (rail highlight). */
export const STEP_TO_DIMENSION = Object.freeze({
  purpose: 'S',
  healthIntro: 'S',
  healthDimensions: 'S',
  whoSelfCheck: 'M',
  competitionYears: 'S',
  style: 'S',
  primaryFocus: 'S',
  secondaryFocus: 'S',
  healthDelivery: 'R',
  healthIntensity: 'A',
  experience: 'M',
  level: 'M',
  customLevel: 'M',
  trainingVolume: 'M',
  country: 'A',
  club: 'A',
  grip: 'A',
  hand: 'A',
  finalObjective: 'A',
  milestoneTargets: 'A',
  relevanceWhy: 'R',
  // identity — does not drive R completeness
  gender: 'R',
  dob: 'R',
  firstName: 'R',
  lastName: 'R',
  name: 'R',
  email: 'R',
  password: 'R',
  cycleLength: 'T',
  funChallengePreview: 'T',
  healthSnapshot: 'T',
  assessment: 'T'
});

/** Canonical order for clearing downstream answers when editing a past step. */
export const STEP_SEQUENCE = Object.freeze([
  'purpose',
  'healthIntro',
  'healthDimensions',
  'whoSelfCheck',
  'competitionYears',
  'experience',
  'style',
  'primaryFocus',
  'secondaryFocus',
  'healthDelivery',
  'healthIntensity',
  'country',
  'club',
  'level',
  'customLevel',
  'trainingVolume',
  'grip',
  'hand',
  'finalObjective',
  'milestoneTargets',
  'relevanceWhy',
  'gender',
  'dob',
  'cycleLength',
  'funChallengePreview',
  'healthSnapshot',
  'firstName',
  'lastName',
  'email',
  'password',
  'assessment'
]);

/**
 * Technical sprint length (T-short / CB path).
 * Always defaults to 3 weeks; player can request a change with coach agreement.
 * Competition ★/· SMART objectives use the 3-week T-short sprint for exercises.
 * Season results (classement/team) use a separate half-season clock (T-long).
 */
export const DEFAULT_TECHNICAL_SPRINT_WEEKS = 3;

export const CYCLE_LENGTH_OPTIONS = Object.freeze([
  { weeks: DEFAULT_TECHNICAL_SPRINT_WEEKS, recommended: true }
]);

/** Competition tenure bands (Specific context for theme / CB / exercise selection). */
export const COMPETITION_YEARS_OPTIONS = Object.freeze([
  { band: 'lt1', years: 0.5 },
  { band: '1-2', years: 1.5 },
  { band: '3-5', years: 4 },
  { band: '5-10', years: 7 },
  { band: '10plus', years: 12 }
]);

/**
 * Clear slot fields for `step` and every later step.
 */
export function clearSlotsFromStep(slots, step, { preserveInvitationIdentity = true, invitation = null } = {}) {
  const idx = STEP_SEQUENCE.indexOf(step);
  if (idx < 0) return { slots, resetCoachAgreement: false };

  const next = {
    S: { ...slots.S, labels: { ...(slots.S.labels || {}) } },
    M: { ...slots.M, labels: { ...(slots.M.labels || {}) }, metric: slots.M.metric ? { ...slots.M.metric } : null },
    A: {
      ...slots.A,
      labels: { ...(slots.A.labels || {}) },
      milestones: Array.isArray(slots.A.milestones) ? [...slots.A.milestones] : []
    },
    R: { ...slots.R, labels: { ...(slots.R.labels || {}) } },
    T: { ...slots.T, cycleStart: null, nextCheckpoint: null },
    health: { ...(slots.health || {}) },
    leisure: slots.leisure ? { ...slots.leisure } : slots.leisure
  };

  const clearFrom = new Set(STEP_SEQUENCE.slice(idx));
  let resetCoachAgreement = false;

  if (clearFrom.has('purpose')) {
    next.S.purposeId = null;
    delete next.S.labels.purpose;
    next.health = {
      introSeen: false,
      healthDimensions: [],
      whoSelfCheck: '',
      whoBand: '',
      delivery: '',
      intensity: '',
      coachNote: '',
      snapshotConfirmed: false
    };
  }
  if (clearFrom.has('healthIntro')) {
    next.health.introSeen = false;
  }
  if (clearFrom.has('healthDimensions')) {
    next.health.healthDimensions = [];
    delete next.S.labels.healthDimensions;
  }
  if (clearFrom.has('whoSelfCheck')) {
    next.health.whoSelfCheck = '';
    delete next.M.labels.whoSelfCheck;
  }
  if (clearFrom.has('healthDelivery')) {
    next.health.delivery = '';
    delete next.R.labels.delivery;
  }
  if (clearFrom.has('healthIntensity')) {
    next.health.intensity = '';
    delete next.A.labels.intensity;
  }
  if (clearFrom.has('healthSnapshot')) {
    next.health.snapshotConfirmed = false;
  }
  if (clearFrom.has('competitionYears')) {
    next.S.competitionYears = null;
    next.S.competitionYearsBand = null;
    delete next.S.labels.competitionYears;
  }
  if (clearFrom.has('experience')) {
    next.M.experienceLevelId = null;
    delete next.M.labels.experience;
  }
  if (clearFrom.has('style')) {
    next.S.styleId = null;
    delete next.S.labels.style;
  }
  if (clearFrom.has('primaryFocus')) {
    next.S.primaryFocusId = null;
    delete next.S.labels.primaryFocus;
  }
  if (clearFrom.has('secondaryFocus')) {
    next.S.secondaryFocusIds = [];
    delete next.S.labels.secondaryFocus;
  }
  if (clearFrom.has('country')) {
    next.A.country = invitation?.country ?? null;
    delete next.A.labels.country;
    if (invitation?.country) next.A.labels.country = invitation.country;
  }
  if (clearFrom.has('club')) {
    next.A.clubId = invitation?.club_id ?? null;
    delete next.A.labels.club;
  }
  if (clearFrom.has('level')) {
    next.M.levelId = null;
    delete next.M.labels.level;
    next.S.classement = invitation?.fltt_classement || null;
    next.S.rankingPoints =
      invitation?.fltt_ranking_points != null
        ? Number(invitation.fltt_ranking_points)
        : null;
    if (!invitation?.fltt_classement) delete next.S.labels.classement;
    if (invitation?.fltt_ranking_points == null) delete next.S.labels.rankingPoints;
  }
  if (clearFrom.has('customLevel')) {
    next.M.customLevel = invitation?.fltt_classement || '';
    if (!invitation?.fltt_classement) delete next.M.labels.level;
    next.S.classement = invitation?.fltt_classement || null;
    if (!invitation?.fltt_classement) delete next.S.labels.classement;
  }
  // Player no longer inputs metricTarget — exercise-defined metrics live outside Digiping capture.
  // Clear any legacy M.metric when reopening from trainingVolume or earlier.
  if (clearFrom.has('trainingVolume')) {
    next.M.metric = null;
    delete next.M.labels.metric;
    next.M.weeklyTraining = null;
    delete next.M.labels.weeklyTraining;
  }
  if (clearFrom.has('grip')) {
    next.A.grip = null;
    delete next.A.labels.grip;
  }
  if (clearFrom.has('hand')) {
    next.A.hand = null;
    delete next.A.labels.hand;
  }
  if (clearFrom.has('finalObjective')) {
    next.A.finalObjective = null;
    if (next.A.seasonQuest) {
      const sq = { ...next.A.seasonQuest };
      delete sq.finalObjective;
      next.A.seasonQuest = Object.keys(sq).length ? sq : null;
    }
    resetCoachAgreement = true;
  }
  if (clearFrom.has('milestoneTargets')) {
    next.A.milestones = [];
    next.A.resourceNote = '';
    delete next.A.labels.milestones;
    resetCoachAgreement = true;
  }
  if (clearFrom.has('relevanceWhy')) {
    next.R.whyItMatters = '';
    next.R.alignedPurposeId = null;
    next.R.motivationSummary = '';
    delete next.R.labels.why;
    resetCoachAgreement = true;
  }
  if (clearFrom.has('gender')) {
    next.R.gender = null;
    delete next.R.labels.gender;
  }
  if (clearFrom.has('dob')) {
    next.R.dateOfBirth = null;
    delete next.R.labels.dob;
  }
  if (clearFrom.has('firstName') || clearFrom.has('name')) {
    if (!preserveInvitationIdentity || !invitationFirstName(invitation)) {
      next.R.firstName = '';
      delete next.R.labels.name;
      delete next.R.labels.firstName;
    } else {
      next.R.firstName = invitationFirstName(invitation);
    }
  }
  if (clearFrom.has('lastName') || clearFrom.has('firstName') || clearFrom.has('name')) {
    if (!preserveInvitationIdentity || !invitationLastName(invitation)) {
      next.R.lastName = '';
      delete next.R.labels.lastName;
    } else {
      next.R.lastName = invitationLastName(invitation);
    }
  }
  if (clearFrom.has('email')) {
    if (!preserveInvitationIdentity || !invitation) {
      next.R.email = '';
      delete next.R.labels.email;
    } else if (invitation?.email) {
      next.R.email = invitation.email;
    }
  }
  if (clearFrom.has('password')) {
    next.R.password = '';
  }
  if (clearFrom.has('cycleLength')) {
    next.T.cycleWeeks = null;
    next.T.cycleStart = null;
    next.T.nextCheckpoint = null;
    next.T.reviewLastWeek = true;
    delete next.T.labels?.cycle;
  }

  return { slots: next, resetCoachAgreement };
}

export const DIMENSION_META = Object.freeze({
  en: {
    S: { letter: 'S', label: 'Specific' },
    M: { letter: 'M', label: 'Measurable' },
    A: { letter: 'A', label: 'Achievable' },
    R: { letter: 'R', label: 'Relevant' },
    T: { letter: 'T', label: 'Time-bound' }
  },
  fr: {
    S: { letter: 'S', label: 'Spécifique' },
    M: { letter: 'M', label: 'Mesurable' },
    A: { letter: 'A', label: 'Atteignable' },
    R: { letter: 'R', label: 'Pertinent' },
    T: { letter: 'T', label: 'Temporel' }
  },
  es: {
    S: { letter: 'S', label: 'Específico' },
    M: { letter: 'M', label: 'Medible' },
    A: { letter: 'A', label: 'Alcanzable' },
    R: { letter: 'R', label: 'Relevante' },
    T: { letter: 'T', label: 'Temporal' }
  },
  de: {
    S: { letter: 'S', label: 'Spezifisch' },
    M: { letter: 'M', label: 'Messbar' },
    A: { letter: 'A', label: 'Erreichbar' },
    R: { letter: 'R', label: 'Relevant' },
    T: { letter: 'T', label: 'Terminiert' }
  },
  nl: {
    S: { letter: 'S', label: 'Specifiek' },
    M: { letter: 'M', label: 'Meetbaar' },
    A: { letter: 'A', label: 'Acceptabel' },
    R: { letter: 'R', label: 'Relevant' },
    T: { letter: 'T', label: 'Tijdgebonden' }
  },
  pt: {
    S: { letter: 'S', label: 'Específico' },
    M: { letter: 'M', label: 'Mensurável' },
    A: { letter: 'A', label: 'Atingível' },
    R: { letter: 'R', label: 'Relevante' },
    T: { letter: 'T', label: 'Temporal' }
  }
});

export function getDimensionMeta(lang = 'en') {
  const key = String(lang || 'en').split('-')[0];
  return DIMENSION_META[key] || DIMENSION_META.en;
}

function emptySlots() {
  return {
    S: {
      purposeId: null,
      styleId: null,
      primaryFocusId: null,
      secondaryFocusIds: [],
      competitionYears: null,
      competitionYearsBand: null,
      /** FLTT / federation classement (LU: A1…); also mirrored from M.level when set. */
      classement: null,
      /** FLTT ranking points (TS) when available on tbl_player. */
      rankingPoints: null,
      /** Concrete activity for Specific — assigned closes S on active profiles. */
      personalizedExercise: {
        exerciseId: null,
        proposalId: null,
        title: null,
        summary: null,
        source: null,
        status: null,
        proposedAt: null,
        assignedAt: null,
        assignedBy: null
      },
      /** Designed exercises per onboarding goal (★ primary + · secondary). */
      goalExercises: [],
      labels: {}
    },
    M: {
      experienceLevelId: null,
      levelId: null,
      customLevel: '',
      metric: null,
      weeklyTraining: null, // { sessionsPerWeek: '1'|'2-3'|'4-5'|'5+', hoursPerSession }
      labels: {}
    },
    A: {
      country: null,
      clubId: null,
      grip: null,
      hand: null,
      milestones: [],
      /** Season destination (competition): { targetClassement, teamGoal, label }. */
      finalObjective: null,
      seasonQuest: null,
      resourceNote: '',
      labels: {}
    },
    R: {
      whyItMatters: '',
      alignedPurposeId: null,
      motivationSummary: '',
      gender: null,
      dateOfBirth: null,
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      labels: {}
    },
    T: {
      /** Technical CB sprint length (T-short). Default 3 weeks. Competition ★/· use half-season (T-long). */
      cycleWeeks: null,
      cycleStart: null,
      nextCheckpoint: null,
      /** Last week of the cycle = review progress + plan next cycle / refine objectives. */
      reviewLastWeek: true,
      labels: {}
    },
    health: {
      introSeen: false,
      healthDimensions: [],
      whoSelfCheck: '',
      whoBand: '',
      delivery: '',
      intensity: '',
      coachNote: '',
      snapshotConfirmed: false
    }
  };
}

function emptyStatus() {
  return { S: 'empty', M: 'empty', A: 'empty', R: 'empty', T: 'empty' };
}

function hasMetric(slots) {
  const m = slots.M?.metric;
  if (!m) return false;
  if (m.label) return true;
  if (m.targetValue != null && String(m.targetValue).trim() !== '') return true;
  return false;
}

function hasWeeklyTraining(slots) {
  const w = slots.M?.weeklyTraining;
  if (!w) return false;
  if (isHealthPurpose(slots?.S?.purposeId)) {
    return w.sessionsPerWeek != null;
  }
  return (
    w.sessionsPerWeek != null &&
    (w.hoursPerSession != null || w.hoursPerWeek != null)
  );
}

function hasBaseline(slots, invitation) {
  const purposeId = slots.S?.purposeId;
  if (purposeId === 3) {
    return (
      slots.M?.levelId != null ||
      (slots.M?.customLevel || '').trim() !== '' ||
      !!invitation?.fltt_classement
    );
  }
  return slots.M?.experienceLevelId != null || purposeId == null;
}

function getGoalExercises(slots, goals = []) {
  const s = slots?.S || {};
  let list = Array.isArray(s.goalExercises)
    ? s.goalExercises.map((e) => ({ goalRole: 'primary', attributeId: null, goalLabel: null, ...e }))
    : [];
  const legacy = s.personalizedExercise;
  const primary = goals.find((g) => g.is_primary) || goals[0] || null;
  const secondaries = goals.filter((g) => !g.is_primary);
  if (!list.length && (legacy?.title || legacy?.status)) {
    list.push({
      goalRole: 'primary',
      attributeId: primary?.attribute_id ?? s.primaryFocusId ?? null,
      goalLabel: primary?.label ?? s.labels?.primaryFocus ?? null,
      ...legacy
    });
  }
  const ensureEntry = (goalRole, goal) => {
    const attrId = goal?.attribute_id ?? (goalRole === 'primary' ? s.primaryFocusId : null);
    if (goalRole === 'secondary' && attrId == null) return;
    const idx = list.findIndex(
      (e) =>
        e.goalRole === goalRole &&
        (attrId == null || String(e.attributeId) === String(attrId))
    );
    if (idx < 0) {
      list.push({
        goalRole,
        attributeId: attrId,
        goalLabel: goal?.label ?? null,
        exerciseId: null,
        title: null,
        summary: null,
        status: null
      });
    }
  };
  if (primary) ensureEntry('primary', primary);
  for (const sec of secondaries) ensureEntry('secondary', sec);
  return list;
}

function hasAssignedGoalExercises(slots, goals = []) {
  const list = getGoalExercises(slots, goals);
  const primaryOk = list.some(
    (e) => e.goalRole === 'primary' && String(e.status || '') === 'assigned'
  );
  if (!primaryOk) return false;
  const secondaryIds = goals.length
    ? goals.filter((g) => !g.is_primary).map((g) => g.attribute_id)
    : slots?.S?.secondaryFocusIds || [];
  if (!secondaryIds.length) return true;
  return secondaryIds.every((id) =>
    list.some(
      (e) =>
        e.goalRole === 'secondary' &&
        String(e.attributeId) === String(id) &&
        String(e.status || '') === 'assigned'
    )
  );
}

/**
 * Derive per-dimension completeness. Coach agreement keeps A/R partial until approved.
 */
export function deriveStatus(slots, ctx = {}) {
  const invitation = ctx.invitation || null;
  const agreement = ctx.coachAgreement || emptyCoachAgreement();
  const coachPending = agreement.status === 'pending';
  const coachApproved = agreement.status === 'approved';

  const purposeId = slots.S?.purposeId;
  const healthPath = isHealthPurpose(purposeId);
  const healthDims = Array.isArray(slots.health?.healthDimensions)
    ? slots.health.healthDimensions
    : [];
  const sPartial =
    purposeId != null ||
    slots.S?.styleId != null ||
    slots.S?.primaryFocusId != null ||
    healthDims.length > 0 ||
    (slots.S?.secondaryFocusIds || []).length > 0;
  const sBase = healthPath
    ? purposeId != null && slots.S?.primaryFocusId != null && healthDims.length > 0
    : purposeId != null && slots.S?.styleId != null && slots.S?.primaryFocusId != null;
  const phase = ctx.phase || null;
  const earlyPhase =
    !phase ||
    phase === PHASES.INTRO ||
    phase === PHASES.CAPTURE ||
    phase === PHASES.ASSESSMENT;
  const coachDev = ctx.profileKind === 'coach_dev';
  const goals = ctx.goals || [];
  const sExerciseAssigned = hasAssignedGoalExercises(slots, goals);
  const sComplete = sBase && (coachDev || earlyPhase || sExerciseAssigned);

  const baselineOk = hasBaseline(slots, invitation);
  const volumeOk = hasWeeklyTraining(slots);
  let mStatus = 'empty';
  // Measurable completeness: baseline + weekly training volume.
  // Concrete exercise metrics (total_stroke_count, max_rally, …) come from training analysis, not Digiping input.
  if (baselineOk && volumeOk) mStatus = 'complete';
  else if (
    baselineOk ||
    volumeOk ||
    slots.M?.experienceLevelId != null ||
    slots.M?.levelId != null ||
    hasMetric(slots)
  ) {
    mStatus = 'partial';
  }

  const leisurePath = isLeisurePurpose(slots?.S?.purposeId);
  const milestones = slots.A?.milestones || [];
  const hasMilestone = milestones.length > 0;
  const hasFinalObjective = Boolean(String(slots.A?.finalObjective?.label || '').trim());
  const aContext = healthPath
    ? Boolean(slots.health?.intensity)
    : leisurePath
      ? true
      : slots.A?.grip != null && slots.A?.hand != null;
  const aCaptured = aContext && (hasMilestone || hasFinalObjective || healthPath);
  const aPartial =
    aCaptured ||
    slots.A?.country != null ||
    slots.A?.clubId != null ||
    slots.A?.grip != null ||
    hasMilestone;

  let aStatus = 'empty';
  if (aCaptured && coachApproved) aStatus = 'complete';
  else if (aCaptured && coachPending) aStatus = 'partial';
  else if (aCaptured) aStatus = 'partial';
  else if (aPartial) aStatus = 'partial';

  const whyOk = !!(slots.R?.whyItMatters && String(slots.R.whyItMatters).trim());
  let rStatus = 'empty';
  if (whyOk && coachApproved) rStatus = 'complete';
  else if (whyOk) rStatus = 'partial';
  else if (slots.R?.gender != null || slots.R?.dateOfBirth) rStatus = 'empty';

  let tStatus = 'empty';
  if (slots.T?.cycleStart && slots.T?.cycleWeeks) tStatus = 'complete';
  else if (slots.T?.cycleWeeks) tStatus = 'partial';
  else if (ctx.phase === PHASES.ASSESSMENT) tStatus = 'partial';

  return {
    S: sComplete ? 'complete' : sPartial ? 'partial' : 'empty',
    M: mStatus,
    A: aStatus,
    R: rStatus,
    T: tStatus
  };
}

function pushActivity(state, type, payload) {
  return [...(state.activities || []), createActivityEvent(type, payload)];
}

function withDerived(state, ctx) {
  const status = deriveStatus(state.slots, {
    ...ctx,
    phase: state.phase,
    invitation: ctx.invitation,
    coachAgreement: state.coachAgreement
  });
  const activeDimension =
    STEP_TO_DIMENSION[state.captureStep] || state.activeDimension || 'S';
  return { ...state, status, activeDimension };
}

export function createInitialState(prefill = {}) {
  const slots = emptySlots();
  if (prefill.email) slots.R.email = prefill.email;
  if (prefill.firstName) slots.R.firstName = prefill.firstName;
  if (prefill.lastName) slots.R.lastName = prefill.lastName;
  if (prefill.country) slots.A.country = prefill.country;
  if (prefill.clubId) slots.A.clubId = prefill.clubId;
  if (prefill.customLevel) slots.M.customLevel = prefill.customLevel;

  const state = {
    phase: PHASES.INTRO,
    captureStep: 'purpose',
    activeDimension: 'S',
    slots,
    status: emptyStatus(),
    activities: [],
    cycleProgress: 0,
    coachAgreement: emptyCoachAgreement()
  };
  return withDerived(state, { invitation: prefill.invitation || null });
}

export function slotsToFormData(slots) {
  return {
    mainPurposeId: slots.S.purposeId,
    competitionYears: slots.S.competitionYears,
    competitionYearsBand: slots.S.competitionYearsBand,
    experienceLevelId: slots.M.experienceLevelId,
    styleId: slots.S.styleId,
    primaryFocusId: slots.S.primaryFocusId,
    primaryFocusCustomText: '',
    secondaryFocusIds: slots.S.secondaryFocusIds || [],
    secondaryFocusCustomText: '',
    levelId: slots.M.levelId,
    customLevel: slots.M.customLevel || '',
    country: slots.A.country,
    clubId: slots.A.clubId,
    grip: slots.A.grip,
    hand: slots.A.hand,
    gender: slots.R.gender,
    dateOfBirth: slots.R.dateOfBirth,
    firstName: slots.R.firstName || '',
    lastName: slots.R.lastName || '',
    email: slots.R.email || '',
    password: slots.R.password || '',
    healthProfile: slots.health || null
  };
}

export function isLeisureSimplifiedPath(slots) {
  return isLeisurePurpose(slots?.S?.purposeId) && isLeisureBeginnerOrIntermediate(slots?.M?.experienceLevelId);
}

export function isLeisurePresetPath(slots) {
  return isLeisurePurpose(slots?.S?.purposeId);
}

export function isHealthPresetPath(slots) {
  return isHealthPurpose(slots?.S?.purposeId);
}

function mergeHealth(slots, patch) {
  return { ...(slots.health || {}), ...patch };
}

function applyHealthPreset(slots, lang) {
  const applied = applyHealthPresetToSlots(slots, { lang: lang || 'en' });
  return {
    ...applied.slots,
    health: {
      ...(applied.slots.health || {}),
      snapshotConfirmed: false,
      questTitle: applied.preset.questTitle
    }
  };
}

function ensureHealthSilentCycle(slots) {
  if (!isHealthPresetPath(slots)) return slots;
  if (slots?.T?.cycleWeeks != null) return slots;
  return {
    ...slots,
    T: {
      ...(slots.T || {}),
      cycleWeeks: DEFAULT_HEALTH_PHASE_WEEKS,
      reviewLastWeek: true,
      labels: {
        ...(slots.T?.labels || {}),
        cycle: slots.T?.labels?.cycle || '4-month health phase'
      }
    }
  };
}

/** Leisure: after volume → gender/dob if needed, then 3-week cycle (T). Account fields come after T. */
function nextAfterTrainingVolume(slots, ctx = {}) {
  if (isHealthPresetPath(slots)) return 'experience';
  if (isLeisurePresetPath(slots)) {
    if (!slots?.R?.gender) return 'gender';
    if (!slots?.R?.dateOfBirth) return 'dob';
    return 'cycleLength';
  }
  return 'grip';
}

/** After Time-bound (T): collect account identity, then assessment. */
function nextAfterTimeBound(slots, ctx = {}) {
  if (!ctx.hasUser) {
    if (!hasFirstName(slots, ctx.invitation)) return 'firstName';
    if (!hasLastName(slots, ctx.invitation)) return 'lastName';
    if (!ctx.invitation?.email && !String(slots?.R?.email || '').trim()) return 'email';
    if (!String(slots?.R?.password || '').trim()) return 'password';
  }
  return 'assessment';
}

function nextAfterCycleLength(slots, ctx = {}) {
  return nextAfterTimeBound(slots, ctx);
}

function ensureLeisureSilentCycle(slots) {
  if (!isLeisurePresetPath(slots)) return slots;
  if (slots?.T?.cycleWeeks != null) return slots;
  return {
    ...slots,
    T: {
      ...(slots.T || {}),
      cycleWeeks: DEFAULT_TECHNICAL_SPRINT_WEEKS,
      reviewLastWeek: true,
      labels: {
        ...(slots.T?.labels || {}),
        cycle: slots.T?.labels?.cycle || '3-week fun block'
      }
    }
  };
}

function classementClubFromInvitation(slots, invitation) {
  return {
    country: invitation?.club_country || invitation?.country || slots?.A?.country || null,
    club_country: invitation?.club_country || invitation?.country || null,
    club_id: invitation?.club_id || slots?.A?.clubId || null,
    club_code: invitation?.club_code || null,
    club_name: invitation?.club_name || null,
    club_address: invitation?.club_address || null,
    city: invitation?.city || null,
    postal_code: invitation?.postal_code || null,
  };
}

/**
 * Country used for official classement wording.
 * The club being joined wins: FLTT is the Luxembourg federation, not the player's passport.
 */
export function profilingClassementCountry(slots, invitation) {
  const club = classementClubFromInvitation(slots, invitation);
  if (isBelgiumClub(club)) return 'BE';
  if (club.club_id || club.club_code || club.club_name) {
    return club.club_country || club.country || slots?.A?.country || null;
  }
  return slots?.A?.country || invitation?.country || null;
}

/** FLTT wording only when the club is Luxembourg — never for Belgian clubs. */
export function isLuxembourgClassementContext(slots, invitation) {
  const club = classementClubFromInvitation(slots, invitation);
  if (isBelgiumClub(club)) return false;
  return isLuxembourgClubCountry(profilingClassementCountry(slots, invitation));
}

/**
 * After style (competition): country / club / level, then final results.
 * LU invite with known classement skips level and goes to final results.
 */
export function nextAfterCompetitionStyle(slots, invitation) {
  const step = nextAfterSecondaryFocus(slots, invitation);
  if (step === 'trainingVolume') return 'finalObjective';
  return step;
}

/**
 * After secondary focus: competition country/club/level path, else club/grip path.
 * Paths that previously jumped to grip now land on trainingVolume first.
 */
export function nextAfterSecondaryFocus(slots, invitation) {
  const purposeId = slots.S.purposeId;
  if (isHealthPurpose(purposeId)) return 'healthDelivery';
  if (purposeId === 3) {
    if (invitation?.country || invitation?.club_id || invitation?.club_code) {
      if (invitation.club_id) {
        if (invitation.fltt_classement && isLuxembourgClassementContext(slots, invitation)) {
          return 'trainingVolume';
        }
        return 'level';
      }
      if (isLuxembourgClassementContext(slots, invitation)) return 'club';
      return 'level';
    }
    return 'country';
  }
  if (invitation?.club_id) return 'trainingVolume';
  return 'club';
}

function invitationFirstName(invitation) {
  return String(invitation?.first_name || invitation?.firstName || '').trim();
}

function invitationLastName(invitation) {
  return String(invitation?.last_name || invitation?.lastName || '').trim();
}

function slotsFirstName(slots) {
  return String(slots?.R?.firstName || '').trim();
}

function slotsLastName(slots) {
  const last = String(slots?.R?.lastName || '').trim();
  if (!last || last.toLowerCase() === 'user') return '';
  return last;
}

function hasFirstName(slots, invitation) {
  return Boolean(slotsFirstName(slots) || invitationFirstName(invitation));
}

function hasLastName(slots, invitation) {
  return Boolean(slotsLastName(slots) || invitationLastName(invitation));
}

function nextIdentityAccountStep(ctx, slots) {
  const invitation = ctx.invitation || null;
  if (!hasFirstName(slots, invitation)) return 'firstName';
  if (!hasLastName(slots, invitation)) return 'lastName';
  if (invitation?.email) return 'password';
  return 'email';
}

function healthSignupComplete(slots, ctx = {}) {
  if (ctx.hasUser) return true;
  const r = slots?.R || {};
  return Boolean(
    r.gender &&
      r.dateOfBirth &&
      hasFirstName(slots, ctx.invitation) &&
      hasLastName(slots, ctx.invitation) &&
      (ctx.invitation?.email || String(r.email || '').trim()) &&
      String(r.password || '').trim()
  );
}

function nextAfterHealthPurpose(ctx = {}) {
  return ctx.hasUser ? 'healthIntro' : 'gender';
}

function nextAfterDob(ctx, slots) {
  if (isHealthPresetPath(slots)) return 'healthIntro';
  return goToCycleOrAssessment(slots);
}

function goToCycleOrAssessment(slots) {
  if (isHealthPresetPath(slots)) {
    return slots?.health?.snapshotConfirmed === true ? 'assessment' : 'healthSnapshot';
  }
  if (isLeisurePresetPath(slots)) {
    return slots?.T?.cycleWeeks ? 'assessment' : 'cycleLength';
  }
  return slots?.T?.cycleWeeks ? 'assessment' : 'cycleLength';
}

/** Whether a capture step already has a stored answer in slots. */
export function isStepAnswered(slots, step, ctx = {}) {
  const s = slots || {};
  switch (step) {
    case 'purpose':
      return s.S?.purposeId != null;
    case 'healthIntro':
      return s.health?.introSeen === true;
    case 'healthDimensions':
      return Array.isArray(s.health?.healthDimensions) && s.health.healthDimensions.length > 0;
    case 'whoSelfCheck':
      return Boolean(s.health?.whoSelfCheck);
    case 'healthDelivery':
      return Boolean(s.health?.delivery);
    case 'healthIntensity':
      return Boolean(s.health?.intensity);
    case 'healthSnapshot':
      return s.health?.snapshotConfirmed === true;
    case 'competitionYears':
      return s.S?.competitionYearsBand != null || s.S?.competitionYears != null;
    case 'experience':
      return s.M?.experienceLevelId != null;
    case 'style':
      return s.S?.styleId != null;
    case 'primaryFocus':
      return s.S?.primaryFocusId != null;
    case 'secondaryFocus':
      return s.S?.labels?.secondaryFocus != null;
    case 'country':
      return Boolean(s.A?.country);
    case 'club':
      return s.A?.clubId != null;
    case 'level':
      return s.M?.levelId != null;
    case 'customLevel':
      return Boolean(s.M?.customLevel) || Boolean(s.M?.labels?.level);
    case 'trainingVolume':
      return s.M?.weeklyTraining?.sessionsPerWeek != null;
    case 'grip':
      return Boolean(s.A?.grip);
    case 'hand':
      return Boolean(s.A?.hand);
    case 'finalObjective':
      return Boolean(String(s.A?.finalObjective?.label || '').trim());
    case 'milestoneTargets':
      return Array.isArray(s.A?.milestones) && s.A.milestones.length > 0;
    case 'relevanceWhy':
      return Boolean(String(s.R?.whyItMatters || '').trim());
    case 'gender':
      return Boolean(s.R?.gender);
    case 'dob':
      return Boolean(s.R?.dateOfBirth);
    case 'firstName':
    case 'name':
      return Boolean(slotsFirstName(s));
    case 'lastName':
      return Boolean(slotsLastName(s));
    case 'email':
      return Boolean(String(s.R?.email || '').trim());
    case 'password':
      return ctx.hasUser || Boolean(String(s.R?.password || '').trim());
    case 'cycleLength':
      return s.T?.cycleWeeks != null;
    case 'funChallengePreview':
      return s.leisure?.funChallengeConfirmed === true;
    case 'assessment':
      return s.T?.cycleWeeks != null;
    default:
      return false;
  }
}

/** Whether a step is part of the path for these slots (branching wizard). */
export function isStepRequired(step, slots, ctx = {}) {
  const invitation = ctx.invitation || null;
  const purposeId = slots?.S?.purposeId;
  const healthPath = isHealthPurpose(purposeId);
  if (step === 'competitionYears') return purposeId === 3;
  if (step === 'experience') {
    if (healthPath) return slots?.M?.weeklyTraining?.sessionsPerWeek != null;
    return purposeId !== 3;
  }
  if (step === 'healthIntro' || step === 'healthDimensions' || step === 'whoSelfCheck') {
    if (!healthPath) return false;
    // Gender/DOB first; name/password come after the T (health snapshot) step.
    return Boolean(ctx.hasUser || (slots?.R?.gender && slots?.R?.dateOfBirth));
  }
  if (step === 'healthDelivery' || step === 'healthIntensity' || step === 'healthSnapshot') {
    if (!healthPath) return false;
    return Boolean(ctx.hasUser || (slots?.R?.gender && slots?.R?.dateOfBirth));
  }
  if (healthPath) {
    if (step === 'style' || step === 'country' || step === 'club' || step === 'level' || step === 'customLevel') {
      return false;
    }
    if (step === 'grip' || step === 'hand' || step === 'finalObjective' || step === 'milestoneTargets' || step === 'relevanceWhy') {
      return false;
    }
    if (step === 'primaryFocus') return Boolean(slots?.health?.whoSelfCheck);
    if (step === 'secondaryFocus') return false;
    if (step === 'trainingVolume') return Boolean(slots?.health?.intensity);
    if (step === 'gender' || step === 'dob') {
      return !ctx.hasUser;
    }
    if (step === 'cycleLength' || step === 'funChallengePreview') return false;
  }
  if (step === 'password') return !ctx.hasUser;
  if (step === 'country') {
    if (purposeId !== 3) return false;
    return !invitation?.country;
  }
  if (step === 'club') {
    if (purposeId === 3 && isLuxembourgClassementContext(slots, invitation)) {
      return !invitation?.club_id;
    }
    if (purposeId !== 3) return !invitation?.club_id;
    return false;
  }
  if (step === 'level') {
    return purposeId === 3 && !invitation?.fltt_classement;
  }
  if (step === 'customLevel') {
    // Same chip options for every country for now — free-text step unused.
    return false;
  }
  if (step === 'firstName' || step === 'name') {
    if (ctx.hasUser) return false;
    return !hasFirstName(slots, invitation);
  }
  if (step === 'lastName') {
    if (ctx.hasUser) return false;
    return !hasLastName(slots, invitation);
  }
  if (step === 'email') {
    return !ctx.hasUser && !invitation?.email;
  }
  if (isLeisurePresetPath(slots)) {
    if (step === 'secondaryFocus') return false;
    if (step === 'grip' || step === 'hand') return false;
    if (step === 'finalObjective' || step === 'milestoneTargets' || step === 'relevanceWhy') return false;
    if (step === 'cycleLength') return true;
    if (step === 'funChallengePreview') return false;
  }
  if (purposeId === 3) {
    if (step === 'primaryFocus') {
      return Boolean(String(slots?.A?.finalObjective?.label || '').trim());
    }
    if (step === 'secondaryFocus') return Boolean(slots?.S?.primaryFocusId);
    if (step === 'milestoneTargets') return false;
    if (step === 'finalObjective') return true;
  }
  if (step === 'finalObjective') return false;
  if (step === 'cycleLength') return purposeId !== 1;
  if (step === 'funChallengePreview') return false;
  return step !== 'assessment';
}

/** Infer the next unanswered capture step from saved slots (fallback when captureStep missing). */
export function inferResumeCaptureStep(slots, ctx = {}) {
  for (const step of STEP_SEQUENCE) {
    if (step === 'assessment') break;
    if (!isStepRequired(step, slots, ctx)) continue;
    if (!isStepAnswered(slots, step, ctx)) return step;
  }
  if (slots?.T?.cycleWeeks) return 'assessment';
  if (isHealthPresetPath(slots)) return 'healthSnapshot';
  if (isLeisurePresetPath(slots)) return 'cycleLength';
  return 'cycleLength';
}

/** True when the wizard has partial SMART capture data worth resuming. */
export function hasWizardProgress(slots) {
  if (!slots) return false;
  return (
    slots.S?.purposeId != null ||
    slots.M?.experienceLevelId != null ||
    slots.S?.styleId != null ||
    Boolean(slots.R?.whyItMatters) ||
    Boolean(slots.A?.grip)
  );
}

export function transition(state, event, ctx = {}) {
  const invitation = ctx.invitation || null;
  const type = event?.type;
  const p = event?.payload || {};

  if (!type) return state;

  switch (type) {
    case 'BEGIN': {
      const purposeId = p.purposeId;
      if (isHealthPurpose(purposeId) && ctx.hasUser) {
        const slots = {
          ...state.slots,
          S: {
            ...state.slots.S,
            purposeId: Number(purposeId),
            labels: { ...state.slots.S.labels, purpose: p.label || 'Health' }
          },
          R: {
            ...state.slots.R,
            alignedPurposeId: Number(purposeId),
            dateOfBirth: p.dateOfBirth || state.slots.R.dateOfBirth,
            gender: p.gender || state.slots.R.gender,
            firstName: p.firstName || state.slots.R.firstName,
            lastName: p.lastName || state.slots.R.lastName,
            email: p.email || state.slots.R.email
          },
          health: mergeHealth(state.slots, { introSeen: false })
        };
        return withDerived(
          {
            ...state,
            slots,
            phase: PHASES.CAPTURE,
            captureStep: 'healthIntro',
            activeDimension: 'S'
          },
          { invitation, hasUser: true }
        );
      }
      return withDerived(
        {
          ...state,
          phase: PHASES.CAPTURE,
          captureStep: 'purpose',
          activeDimension: 'S'
        },
        { invitation }
      );
    }

    case 'REOPEN_STEP': {
      const step = p.step === 'name' ? 'firstName' : p.step;
      if (!step || step === 'assessment') return state;
      if (!STEP_SEQUENCE.includes(step)) return state;
      const cleared = clearSlotsFromStep(state.slots, step, {
        preserveInvitationIdentity: true,
        invitation
      });
      return withDerived(
        {
          ...state,
          slots: cleared.slots,
          captureStep: step,
          phase: PHASES.CAPTURE,
          activeDimension: STEP_TO_DIMENSION[step] || state.activeDimension,
          coachAgreement: cleared.resetCoachAgreement
            ? emptyCoachAgreement()
            : state.coachAgreement
        },
        { invitation }
      );
    }

    case 'ANSWER_PURPOSE': {
      const purposeId = p.purposeId;
      const slots = {
        ...state.slots,
        S: {
          ...state.slots.S,
          purposeId,
          labels: { ...state.slots.S.labels, purpose: p.label }
        },
        R: {
          ...state.slots.R,
          alignedPurposeId: purposeId
        }
      };
      // Competition → years in competition (Specific context) then style
      const nextStep = purposeId === 3
        ? 'competitionYears'
        : purposeId === 1
          ? nextAfterHealthPurpose({ hasUser: ctx.hasUser, invitation })
          : 'experience';
      return withDerived(
        { ...state, slots, captureStep: nextStep, phase: PHASES.CAPTURE },
        { invitation }
      );
    }

    case 'ANSWER_HEALTH_INTRO': {
      const slots = {
        ...state.slots,
        health: mergeHealth(state.slots, { introSeen: true })
      };
      return withDerived(
        { ...state, slots, captureStep: 'healthDimensions', phase: PHASES.CAPTURE },
        { invitation }
      );
    }

    case 'ANSWER_HEALTH_DIMENSIONS': {
      const dims = Array.isArray(p.healthDimensions) ? p.healthDimensions : [];
      const slots = {
        ...state.slots,
        health: mergeHealth(state.slots, { healthDimensions: dims }),
        S: {
          ...state.slots.S,
          labels: { ...state.slots.S.labels, healthDimensions: p.label || dims.join(', ') }
        }
      };
      return withDerived(
        { ...state, slots, captureStep: 'whoSelfCheck', phase: PHASES.CAPTURE, activeDimension: 'M' },
        { invitation }
      );
    }

    case 'ANSWER_WHO_SELF_CHECK': {
      const slots = {
        ...state.slots,
        health: mergeHealth(state.slots, {
          whoSelfCheck: p.whoSelfCheck,
          whoBand: p.whoBand || state.slots.health?.whoBand || ''
        }),
        M: {
          ...state.slots.M,
          labels: { ...state.slots.M.labels, whoSelfCheck: p.label || p.whoSelfCheck }
        }
      };
      return withDerived(
        { ...state, slots, captureStep: 'primaryFocus', phase: PHASES.CAPTURE, activeDimension: 'S' },
        { invitation }
      );
    }

    case 'ANSWER_HEALTH_DELIVERY': {
      const slots = {
        ...state.slots,
        health: mergeHealth(state.slots, { delivery: p.delivery }),
        R: {
          ...state.slots.R,
          labels: { ...state.slots.R.labels, delivery: p.label || p.delivery }
        }
      };
      return withDerived(
        { ...state, slots, captureStep: 'healthIntensity', phase: PHASES.CAPTURE, activeDimension: 'A' },
        { invitation }
      );
    }

    case 'ANSWER_HEALTH_INTENSITY': {
      const slots = {
        ...state.slots,
        health: mergeHealth(state.slots, {
          intensity: p.intensity,
          coachNote: p.coachNote || state.slots.health?.coachNote || ''
        }),
        A: {
          ...state.slots.A,
          labels: { ...state.slots.A.labels, intensity: p.label || p.intensity }
        }
      };
      return withDerived(
        { ...state, slots, captureStep: 'trainingVolume', phase: PHASES.CAPTURE, activeDimension: 'M' },
        { invitation }
      );
    }

    case 'CONFIRM_HEALTH_SNAPSHOT': {
      let slots = {
        ...state.slots,
        health: mergeHealth(state.slots, { snapshotConfirmed: true })
      };
      slots = applyHealthPreset(slots, p.lang);
      slots = ensureHealthSilentCycle(slots);
      const coachAgreement = {
        ...emptyCoachAgreement(),
        status: 'pending',
        at: new Date().toISOString()
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.COACH_AGREEMENT_REQUESTED, {
        whyItMatters: slots.R?.whyItMatters,
        milestones: slots.A?.milestones,
        metric: slots.M?.metric,
        weeklyTraining: slots.M?.weeklyTraining,
        healthQuest: slots.health?.questTitle
      });
      const nextStep = nextAfterTimeBound(slots, ctx);
      const identityPending = nextStep !== 'assessment';
      return withDerived(
        {
          ...state,
          slots,
          coachAgreement,
          captureStep: nextStep,
          phase: identityPending ? PHASES.CAPTURE : PHASES.ASSESSMENT,
          activeDimension: identityPending ? 'R' : 'T',
          activities
        },
        { invitation, hasUser: ctx.hasUser }
      );
    }

    case 'ANSWER_COMPETITION_YEARS': {
      const slots = {
        ...state.slots,
        S: {
          ...state.slots.S,
          competitionYears: p.years,
          competitionYearsBand: p.band,
          labels: {
            ...state.slots.S.labels,
            competitionYears: p.label
          }
        }
      };
      return withDerived({ ...state, slots, captureStep: 'style' }, { invitation });
    }

    case 'ANSWER_EXPERIENCE': {
      let slots = {
        ...state.slots,
        M: {
          ...state.slots.M,
          experienceLevelId: p.experienceLevelId,
          labels: { ...state.slots.M.labels, experience: p.label }
        }
      };
      if (isHealthPresetPath(slots)) {
        slots = applyHealthPreset(slots, p.lang);
        slots = ensureHealthSilentCycle(slots);
        return withDerived(
          { ...state, slots, captureStep: 'healthSnapshot', phase: PHASES.CAPTURE, activeDimension: 'T' },
          { invitation }
        );
      }
      return withDerived({ ...state, slots, captureStep: 'style' }, { invitation });
    }

    case 'ANSWER_STYLE': {
      const slots = {
        ...state.slots,
        S: {
          ...state.slots.S,
          styleId: p.styleId,
          labels: { ...state.slots.S.labels, style: p.label }
        }
      };
      return withDerived(
        { ...state, slots, captureStep: Number(state.slots.S?.purposeId) === 3
          ? nextAfterCompetitionStyle(slots, invitation)
          : 'primaryFocus' },
        { invitation }
      );
    }

    case 'ANSWER_PRIMARY_FOCUS': {
      let slots = {
        ...state.slots,
        S: {
          ...state.slots.S,
          primaryFocusId: p.primaryFocusId,
          labels: { ...state.slots.S.labels, primaryFocus: p.label }
        }
      };
      if (isLeisurePresetPath(slots)) {
        const lang = p.lang || 'en';
        const applied = applyLeisurePresetToSlots(slots, {
          primaryFocusId: p.primaryFocusId,
          experienceLevelId: slots.M?.experienceLevelId,
          dateOfBirth: slots.R?.dateOfBirth,
          lang
        });
        slots = {
          ...applied.slots,
          leisure: {
            questTitle: applied.preset.questTitle,
            presetApplied: true,
            funChallengeConfirmed: false
          }
        };
      }
      const nextStep =
        isHealthPresetPath(slots) || isLeisurePresetPath(slots)
          ? nextAfterSecondaryFocus(slots, invitation)
          : 'secondaryFocus';
      let activities = state.activities;
      if (slots.S.primaryFocusId != null) {
        activities = pushActivity(
          { activities },
          ACTIVITY_TYPES.GOAL_SET,
          { primaryFocusId: slots.S.primaryFocusId, purposeId: slots.S.purposeId }
        );
      }
      return withDerived(
        { ...state, slots, captureStep: nextStep, activities },
        { invitation }
      );
    }

    case 'ANSWER_SECONDARY_FOCUS': {
      const slots = {
        ...state.slots,
        S: {
          ...state.slots.S,
          secondaryFocusIds: p.secondaryFocusIds || [],
          labels: { ...state.slots.S.labels, secondaryFocus: p.label }
        }
      };
      const nextStep = Number(slots.S.purposeId) === 3
        ? 'trainingVolume'
        : nextAfterSecondaryFocus(slots, invitation);
      let activities = state.activities;
      if (slots.S.primaryFocusId != null) {
        activities = pushActivity(
          { activities },
          ACTIVITY_TYPES.GOAL_SET,
          { primaryFocusId: slots.S.primaryFocusId, purposeId: slots.S.purposeId }
        );
      }
      return withDerived(
        { ...state, slots, captureStep: nextStep, activities },
        { invitation }
      );
    }

    case 'ANSWER_COUNTRY': {
      const slots = {
        ...state.slots,
        A: {
          ...state.slots.A,
          country: p.country,
          labels: { ...state.slots.A.labels, country: p.label }
        }
      };
      const nextStep = isLuxembourgClubCountry(p.country) ? 'club' : 'level';
      return withDerived({ ...state, slots, captureStep: nextStep }, { invitation });
    }

    case 'ANSWER_CLUB': {
      const slots = {
        ...state.slots,
        A: {
          ...state.slots.A,
          clubId: p.clubId,
          labels: { ...state.slots.A.labels, club: p.label }
        }
      };
      const nextStep = slots.S.purposeId === 3 ? 'level' : 'trainingVolume';
      return withDerived({ ...state, slots, captureStep: nextStep }, { invitation });
    }

    case 'ANSWER_LEVEL': {
      const classement = p.label || null;
      const rankingPoints =
        p.rankingPoints != null
          ? Number(p.rankingPoints)
          : invitation?.fltt_ranking_points != null
            ? Number(invitation.fltt_ranking_points)
            : state.slots.S?.rankingPoints ?? null;
      const sLabels = { ...state.slots.S.labels };
      if (classement) sLabels.classement = classement;
      if (rankingPoints != null && !Number.isNaN(rankingPoints)) {
        sLabels.rankingPoints = `${rankingPoints} pts`;
      }
      const slots = {
        ...state.slots,
        M: {
          ...state.slots.M,
          levelId: p.levelId,
          // Non-LU profile complete still expects customLevel; reuse the chip label.
          customLevel: p.label || state.slots.M.customLevel || '',
          labels: { ...state.slots.M.labels, level: p.label }
        },
        S: {
          ...state.slots.S,
          classement,
          rankingPoints:
            rankingPoints != null && !Number.isNaN(rankingPoints) ? rankingPoints : null,
          labels: sLabels
        }
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.BASELINE_SET, {
        levelId: p.levelId,
        classement,
        rankingPoints: slots.S.rankingPoints
      });
      return withDerived(
        { ...state, slots, captureStep: 'finalObjective', activities },
        { invitation }
      );
    }

    case 'ANSWER_CUSTOM_LEVEL': {
      const classement = p.label || p.customLevel || null;
      const rankingPoints =
        p.rankingPoints != null
          ? Number(p.rankingPoints)
          : invitation?.fltt_ranking_points != null
            ? Number(invitation.fltt_ranking_points)
            : state.slots.S?.rankingPoints ?? null;
      const sLabels = { ...state.slots.S.labels };
      if (classement) sLabels.classement = classement;
      if (rankingPoints != null && !Number.isNaN(rankingPoints)) {
        sLabels.rankingPoints = `${rankingPoints} pts`;
      }
      const slots = {
        ...state.slots,
        M: {
          ...state.slots.M,
          customLevel: p.customLevel || '',
          labels: { ...state.slots.M.labels, level: p.label || p.customLevel }
        },
        S: {
          ...state.slots.S,
          classement,
          rankingPoints:
            rankingPoints != null && !Number.isNaN(rankingPoints) ? rankingPoints : null,
          labels: sLabels
        }
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.BASELINE_SET, {
        customLevel: p.customLevel,
        classement,
        rankingPoints: slots.S.rankingPoints
      });
      return withDerived(
        { ...state, slots, captureStep: 'finalObjective', activities },
        { invitation }
      );
    }

    // Legacy: Digiping no longer collects player metric targets.
    case 'ANSWER_METRIC': {
      const metric = {
        kind: p.kind || 'custom',
        targetValue: p.targetValue ?? null,
        unit: p.unit || '',
        label: p.label || ''
      };
      const slots = {
        ...state.slots,
        M: {
          ...state.slots.M,
          metric,
          labels: { ...state.slots.M.labels, metric: p.label || metric.label }
        }
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.METRIC_SET, metric);
      return withDerived(
        { ...state, slots, captureStep: 'trainingVolume', activities },
        { invitation }
      );
    }

    case 'ANSWER_TRAINING_VOLUME': {
      const weeklyTraining = {
        sessionsPerWeek: p.sessionsPerWeek ?? null,
        hoursPerSession:
          p.hoursPerSession ??
          (isHealthPurpose(state.slots.S?.purposeId) ? DEFAULT_HEALTH_SESSION_HOURS : null),
        // Legacy field kept null; volume is now hours per session.
        hoursPerWeek: p.hoursPerWeek ?? null
      };
      const label = p.label || '';
      const slots = {
        ...state.slots,
        M: {
          ...state.slots.M,
          weeklyTraining,
          labels: { ...state.slots.M.labels, weeklyTraining: label }
        }
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.TRAINING_VOLUME_SET, weeklyTraining);
      return withDerived(
        {
          ...state,
          slots,
          captureStep: nextAfterTrainingVolume(slots, { hasUser: ctx.hasUser, invitation }),
          activities
        },
        { invitation }
      );
    }

    case 'ANSWER_GRIP': {
      const slots = {
        ...state.slots,
        A: {
          ...state.slots.A,
          grip: p.grip,
          labels: { ...state.slots.A.labels, grip: p.label }
        }
      };
      let activities = state.activities;
      if (
        slots.S.purposeId !== 3 &&
        slots.M.experienceLevelId != null &&
        !activities.some((a) => a.type === ACTIVITY_TYPES.BASELINE_SET)
      ) {
        activities = pushActivity(
          { activities },
          ACTIVITY_TYPES.BASELINE_SET,
          { experienceLevelId: slots.M.experienceLevelId }
        );
      }
      return withDerived(
        { ...state, slots, captureStep: 'hand', activities },
        { invitation }
      );
    }

    case 'ANSWER_HAND': {
      const slots = {
        ...state.slots,
        A: {
          ...state.slots.A,
          hand: p.hand,
          labels: { ...state.slots.A.labels, hand: p.label }
        }
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.CONTEXT_SET, {
        grip: slots.A.grip,
        hand: p.hand,
        country: slots.A.country,
        clubId: slots.A.clubId
      });
      return withDerived(
        {
          ...state,
          slots,
          captureStep: Number(state.slots.S?.purposeId) === 3 ? 'relevanceWhy' : 'milestoneTargets',
          activities
        },
        { invitation }
      );
    }

    case 'ANSWER_FINAL_OBJECTIVE': {
      const kind = (p.kind || '').trim() || null;
      const kinds = Array.isArray(p.kinds) && p.kinds.length
        ? p.kinds.map((id) => String(id || '').trim()).filter(Boolean)
        : kind
          ? [kind]
          : [];
      const customText = (p.customText || '').trim() || null;
      const targetClassement = (p.targetClassement || '').trim() || null;
      const teamGoal = (p.teamGoal || '').trim() || null;
      const label = (p.label || '').trim();
      const resourceNote = p.resourceNote || '';
      const finalObjective = {
        kind: kinds[0] || kind,
        kinds,
        customText,
        targetClassement,
        teamGoal,
        label
      };
      const milestones = label
        ? [
            {
              id: kinds[0] || 'final_objective',
              kind: kinds[0] || 'final_objective',
              horizon: 'long',
              label,
              target: {
                kinds,
                custom: customText,
                classement: targetClassement,
                team: teamGoal
              }
            }
          ]
        : [];
      const seasonQuest = {
        ...(state.slots.A?.seasonQuest || {}),
        finalObjective
      };
      const slots = {
        ...state.slots,
        A: {
          ...state.slots.A,
          finalObjective,
          milestones,
          resourceNote,
          seasonQuest,
          labels: { ...state.slots.A.labels, milestones: label, finalObjective: label }
        }
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.MILESTONES_SET, {
        milestones,
        resourceNote,
        finalObjective
      });
      return withDerived(
        { ...state, slots, captureStep: Number(state.slots.S?.purposeId) === 3 ? 'primaryFocus' : 'relevanceWhy', activities },
        { invitation }
      );
    }

    case 'ANSWER_MILESTONES': {
      const milestones = Array.isArray(p.milestones) ? p.milestones : [];
      const resourceNote = p.resourceNote || '';
      const label = milestones.map((m) => m.label).filter(Boolean).join(' · ');
      const purposeId = Number(state.slots?.S?.purposeId);
      const firstMid = milestones.find((m) => m.horizon === 'mid') || milestones[0];
      let seasonQuest = state.slots.A?.seasonQuest || null;
      if (firstMid && purposeId === 3) {
        const kind = firstMid.expectedResultKind || firstMid.kind || firstMid.id || 'custom';
        seasonQuest = {
          ...(seasonQuest || {}),
          expectedResult: {
            kind,
            target: firstMid.target || { value: firstMid.value, label: firstMid.label },
            label: firstMid.label || label,
          },
        };
      } else if (firstMid?.label) {
        seasonQuest = {
          ...(seasonQuest || {}),
          expectedResult: {
            kind: 'custom',
            target: { label: firstMid.label },
            label: firstMid.label,
          },
        };
      }
      const slots = {
        ...state.slots,
        A: {
          ...state.slots.A,
          milestones,
          resourceNote,
          seasonQuest,
          labels: { ...state.slots.A.labels, milestones: label }
        }
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.MILESTONES_SET, {
        milestones,
        resourceNote
      });
      return withDerived(
        { ...state, slots, captureStep: 'relevanceWhy', activities },
        { invitation }
      );
    }

    case 'ANSWER_RELEVANCE': {
      const why = (p.whyItMatters || p.label || '').trim();
      const slots = {
        ...state.slots,
        R: {
          ...state.slots.R,
          whyItMatters: why,
          alignedPurposeId: state.slots.S.purposeId,
          motivationSummary: why,
          labels: { ...state.slots.R.labels, why }
        }
      };
      const coachAgreement = {
        ...emptyCoachAgreement(),
        status: 'pending',
        at: new Date().toISOString()
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.COACH_AGREEMENT_REQUESTED, {
        whyItMatters: why,
        milestones: slots.A.milestones,
        resourceNote: slots.A.resourceNote,
        metric: slots.M.metric,
        weeklyTraining: slots.M.weeklyTraining
      });
      return withDerived(
        {
          ...state,
          slots,
          coachAgreement,
          captureStep: 'gender',
          activities
        },
        { invitation }
      );
    }

    case 'SET_COACH_AGREEMENT': {
      const status = p.status || 'approved';
      const coachAgreement = {
        ...state.coachAgreement,
        status,
        by: p.by || null,
        at: new Date().toISOString(),
        note: p.note || '',
        revisedMilestones: p.revisedMilestones || state.coachAgreement.revisedMilestones
      };
      let slots = state.slots;
      if (status === 'approved' && Array.isArray(p.revisedMilestones) && p.revisedMilestones.length) {
        slots = {
          ...slots,
          A: {
            ...slots.A,
            milestones: p.revisedMilestones,
            labels: {
              ...slots.A.labels,
              milestones: p.revisedMilestones.map((m) => m.label).filter(Boolean).join(' · ')
            }
          }
        };
      }
      const activities = pushActivity(state, ACTIVITY_TYPES.COACH_AGREEMENT_SET, {
        status,
        note: coachAgreement.note,
        revisedMilestones: coachAgreement.revisedMilestones
      });
      return withDerived(
        { ...state, slots, coachAgreement, activities },
        { invitation }
      );
    }

    case 'ANSWER_GENDER': {
      const slots = {
        ...state.slots,
        R: {
          ...state.slots.R,
          gender: p.gender,
          labels: { ...state.slots.R.labels, gender: p.label }
        }
      };
      return withDerived({ ...state, slots, captureStep: 'dob' }, { invitation });
    }

    case 'ANSWER_DOB': {
      let slots = {
        ...state.slots,
        R: {
          ...state.slots.R,
          dateOfBirth: p.dateOfBirth,
          labels: { ...state.slots.R.labels, dob: p.label }
        }
      };
      // Re-apply leisure challenge once age is known (5 / 10 / 20 forehand rallies).
      if (isLeisurePresetPath(slots) && slots.leisure?.presetApplied) {
        const applied = applyLeisurePresetToSlots(slots, {
          primaryFocusId: slots.S?.primaryFocusId,
          experienceLevelId: slots.M?.experienceLevelId,
          dateOfBirth: p.dateOfBirth,
          lang: p.lang || 'en'
        });
        slots = {
          ...applied.slots,
          leisure: {
            ...(slots.leisure || {}),
            questTitle: applied.preset.questTitle,
            presetApplied: true
          }
        };
      }
      const nextStep = nextAfterDob({ hasUser: ctx.hasUser, invitation }, slots);
      const activities = pushActivity(state, ACTIVITY_TYPES.IDENTITY_SET, {
        gender: slots.R.gender,
        dateOfBirth: p.dateOfBirth
      });
      if (nextStep === 'cycleLength' || nextStep === 'assessment' || nextStep === 'funChallengePreview') {
        const step = goToCycleOrAssessment(slots);
        return withDerived(
          {
            ...state,
            slots,
            captureStep: step,
            phase: step === 'assessment' ? PHASES.ASSESSMENT : PHASES.CAPTURE,
            activities,
            activeDimension: 'T'
          },
          { invitation }
        );
      }
      return withDerived(
        { ...state, slots, captureStep: nextStep, activities },
        { invitation }
      );
    }

    case 'ANSWER_FIRST_NAME': {
      const slots = {
        ...state.slots,
        R: {
          ...state.slots.R,
          firstName: p.firstName || '',
          labels: { ...state.slots.R.labels, firstName: p.label, name: p.label }
        }
      };
      const nextStep = nextIdentityAccountStep({ hasUser: ctx.hasUser, invitation }, slots);
      return withDerived({ ...state, slots, captureStep: nextStep }, { invitation });
    }

    case 'ANSWER_LAST_NAME': {
      const slots = {
        ...state.slots,
        R: {
          ...state.slots.R,
          lastName: p.lastName || '',
          labels: { ...state.slots.R.labels, lastName: p.label }
        }
      };
      const nextStep = nextIdentityAccountStep({ hasUser: ctx.hasUser, invitation }, slots);
      return withDerived({ ...state, slots, captureStep: nextStep }, { invitation });
    }

    case 'ANSWER_NAME': {
      const lastRaw = String(p.lastName || '').trim();
      const lastName = lastRaw.toLowerCase() === 'user' ? '' : lastRaw;
      const slots = {
        ...state.slots,
        R: {
          ...state.slots.R,
          firstName: p.firstName || '',
          lastName,
          labels: { ...state.slots.R.labels, name: p.label, firstName: p.label }
        }
      };
      const nextStep = nextIdentityAccountStep({ hasUser: ctx.hasUser, invitation }, slots);
      return withDerived({ ...state, slots, captureStep: nextStep }, { invitation });
    }

    case 'ANSWER_EMAIL': {
      const slots = {
        ...state.slots,
        R: {
          ...state.slots.R,
          email: p.email || '',
          labels: { ...state.slots.R.labels, email: p.label }
        }
      };
      return withDerived({ ...state, slots, captureStep: 'password' }, { invitation });
    }

    case 'ANSWER_PASSWORD': {
      let slots = {
        ...state.slots,
        R: { ...state.slots.R, password: p.password || '' }
      };
      slots = ensureHealthSilentCycle(slots);
      slots = ensureLeisureSilentCycle(slots);
      return withDerived(
        {
          ...state,
          slots,
          captureStep: 'assessment',
          phase: PHASES.ASSESSMENT,
          activeDimension: 'T'
        },
        { invitation }
      );
    }

    case 'ANSWER_CYCLE_LENGTH': {
      // Technical CB sprint is always 3 weeks by default; longer cycles need coach agreement later.
      const weeks = Number(p.cycleWeeks) > 0 ? Number(p.cycleWeeks) : DEFAULT_TECHNICAL_SPRINT_WEEKS;
      const label =
        p.label ||
        `${weeks}-week technical sprint (change on request with coach agreement)`;
      const slots = {
        ...state.slots,
        T: {
          ...state.slots.T,
          cycleWeeks: weeks,
          reviewLastWeek: true,
          labels: {
            ...(state.slots.T.labels || {}),
            cycle: label
          }
        }
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.TRAINING_VOLUME_SET, {
        cycleWeeks: weeks,
        reviewLastWeek: true
      });
      const nextStep = nextAfterCycleLength(slots, ctx);
      const identityPending = nextStep !== 'assessment';
      return withDerived(
        {
          ...state,
          slots,
          captureStep: nextStep,
          phase: identityPending ? PHASES.CAPTURE : PHASES.ASSESSMENT,
          activeDimension: identityPending ? 'R' : 'T',
          activities
        },
        { invitation, hasUser: ctx.hasUser }
      );
    }

    case 'CONFIRM_FUN_CHALLENGE': {
      let slots = {
        ...state.slots,
        leisure: {
          ...(state.slots.leisure || {}),
          funChallengeConfirmed: true
        }
      };
      slots = ensureLeisureSilentCycle(slots);
      const coachAgreement = {
        ...emptyCoachAgreement(),
        status: 'pending',
        at: new Date().toISOString()
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.COACH_AGREEMENT_REQUESTED, {
        whyItMatters: slots.R?.whyItMatters,
        milestones: slots.A?.milestones,
        metric: slots.M?.metric,
        weeklyTraining: slots.M?.weeklyTraining,
        leisureQuest: slots.leisure?.questTitle
      });
      const nextStep = nextAfterTimeBound(slots, ctx);
      const identityPending = nextStep !== 'assessment';
      return withDerived(
        {
          ...state,
          slots,
          coachAgreement,
          captureStep: nextStep,
          phase: identityPending ? PHASES.CAPTURE : PHASES.ASSESSMENT,
          activeDimension: identityPending ? 'R' : 'T',
          activities
        },
        { invitation, hasUser: ctx.hasUser }
      );
    }

    case 'ENTER_ASSESSMENT': {
      let slots = ensureLeisureSilentCycle(state.slots);
      slots = ensureHealthSilentCycle(slots);
      if (!slots?.T?.cycleWeeks && !isLeisurePresetPath(slots) && !isHealthPresetPath(slots)) {
        return withDerived(
          {
            ...state,
            captureStep: 'cycleLength',
            phase: PHASES.CAPTURE,
            activeDimension: 'T'
          },
          { invitation }
        );
      }
      return withDerived(
        {
          ...state,
          slots,
          captureStep: 'assessment',
          phase: PHASES.ASSESSMENT,
          activeDimension: 'T'
        },
        { invitation }
      );
    }

    case 'COMMIT_PROFILE': {
      const healthPath = isHealthPresetPath(state.slots);
      const weeks =
        Number(state.slots.T.cycleWeeks) > 0
          ? Number(state.slots.T.cycleWeeks)
          : healthPath
            ? DEFAULT_HEALTH_PHASE_WEEKS
            : DEFAULT_TECHNICAL_SPRINT_WEEKS;
      const cycleStart = new Date().toISOString();
      const nextCheckpoint = new Date(
        Date.now() + weeks * 7 * 24 * 60 * 60 * 1000
      ).toISOString();
      const slots = {
        ...state.slots,
        T: {
          ...state.slots.T,
          cycleWeeks: weeks,
          reviewLastWeek: true,
          cycleStart,
          nextCheckpoint
        }
      };
      const activities = pushActivity(state, ACTIVITY_TYPES.CYCLE_STARTED, {
        cycleWeeks: weeks,
        reviewLastWeek: true,
        cycleStart,
        nextCheckpoint
      });
      return withDerived(
        {
          ...state,
          slots,
          activities,
          phase: PHASES.ACTIVE,
          captureStep: 'assessment',
          cycleProgress: 0
        },
        { invitation }
      );
    }

    case 'RECORD_ACTIVITY': {
      const activity = p.activity || createActivityEvent(p.activityType, p.payload);
      const activities = [...(state.activities || []), activity];
      let phase = state.phase;
      let slots = state.slots;
      let coachAgreement = state.coachAgreement;

      if (activity.type === ACTIVITY_TYPES.CYCLE_ENDED) phase = PHASES.REVIEW;
      if (activity.type === ACTIVITY_TYPES.CYCLE_RENEWED) {
        const cycleStart = new Date().toISOString();
        const nextCheckpoint = new Date(
          Date.now() + (slots.T.cycleWeeks || DEFAULT_TECHNICAL_SPRINT_WEEKS) * 7 * 24 * 60 * 60 * 1000
        ).toISOString();
        slots = { ...slots, T: { ...slots.T, cycleStart, nextCheckpoint } };
        phase = PHASES.ACTIVE;
      }
      if (activity.type === ACTIVITY_TYPES.GOALS_REFINED) phase = PHASES.CAPTURE;
      if (activity.type === ACTIVITY_TYPES.COACH_AGREEMENT_SET) {
        coachAgreement = {
          ...coachAgreement,
          ...(activity.payload || {}),
          at: activity.at
        };
      }

      const fromPresence = presenceRatioFromCyclePresence(
        p.cyclePresence || state.cyclePresence
      );
      const cycleProgress =
        fromPresence != null
          ? fromPresence
          : estimateCycleProgress(
              activities,
              slots.T.cycleStart,
              slots.T.cycleWeeks
            );

      return withDerived(
        {
          ...state,
          slots,
          activities,
          phase,
          cycleProgress,
          coachAgreement,
          cyclePresence: p.cyclePresence || state.cyclePresence || null
        },
        { invitation }
      );
    }

    case 'HYDRATE': {
      const slots = p.slots || state.slots;
      const captureStepRaw =
        p.captureStep ||
        inferResumeCaptureStep(slots, { ...ctx, hasUser: ctx.hasUser });
      const captureStep = captureStepRaw === 'name' ? 'firstName' : captureStepRaw;
      const phase =
        p.phase === PHASES.INTRO && hasWizardProgress(slots)
          ? PHASES.CAPTURE
          : p.phase || state.phase;
      return withDerived(
        {
          ...state,
          slots,
          status: p.status || state.status,
          activities: Array.isArray(p.activities) ? p.activities : state.activities,
          coachAgreement: p.coachAgreement ?? state.coachAgreement,
          phase,
          captureStep,
          activeDimension:
            p.activeDimension ||
            STEP_TO_DIMENSION[captureStep] ||
            state.activeDimension
        },
        { invitation }
      );
    }

    case 'PREFILL': {
      const slots = { ...state.slots };
      if (p.email != null) slots.R = { ...slots.R, email: p.email };
      if (p.firstName != null) slots.R = { ...slots.R, firstName: p.firstName };
      if (p.lastName != null) slots.R = { ...slots.R, lastName: p.lastName };
      if (p.gender != null) slots.R = { ...slots.R, gender: p.gender };
      if (p.dateOfBirth != null) slots.R = { ...slots.R, dateOfBirth: p.dateOfBirth };
      if (p.country != null) slots.A = { ...slots.A, country: p.country };
      if (p.clubId != null) slots.A = { ...slots.A, clubId: p.clubId };
      if (p.customLevel != null) slots.M = { ...slots.M, customLevel: p.customLevel };
      const classement = p.classement || p.customLevel || p.fltt_classement || null;
      const rankingPoints =
        p.rankingPoints != null
          ? Number(p.rankingPoints)
          : p.fltt_ranking_points != null
            ? Number(p.fltt_ranking_points)
            : null;
      if (classement || rankingPoints != null) {
        const sLabels = { ...(slots.S.labels || {}) };
        if (classement) sLabels.classement = classement;
        if (rankingPoints != null && !Number.isNaN(rankingPoints)) {
          sLabels.rankingPoints = `${rankingPoints} pts`;
        }
        slots.S = {
          ...slots.S,
          classement: classement || slots.S.classement,
          rankingPoints:
            rankingPoints != null && !Number.isNaN(rankingPoints)
              ? rankingPoints
              : slots.S.rankingPoints,
          labels: sLabels
        };
        if (classement && !slots.M.labels?.level) {
          slots.M = {
            ...slots.M,
            labels: { ...(slots.M.labels || {}), level: classement }
          };
        }
      }
      return withDerived({ ...state, slots }, { invitation });
    }

    default:
      return state;
  }
}

export const STEP_PROMPT_KEY = Object.freeze({
  purpose: 'qPurpose',
  competitionYears: 'qCompetitionYears',
  experience: 'qExperience',
  style: 'qStyle',
  primaryFocus: 'qPrimaryFocus',
  secondaryFocus: 'qSecondaryFocus',
  country: 'qCountry',
  club: 'qClub',
  level: 'qLevelGeneric',
  customLevel: 'qCustomLevel',
  trainingVolume: 'qTrainingVolume',
  grip: 'qGrip',
  hand: 'qHand',
  finalObjective: 'qFinalObjective',
  milestoneTargets: 'qMilestones',
  relevanceWhy: 'qRelevance',
  gender: 'qGender',
  dob: 'qDob',
  firstName: 'qFirstName',
  lastName: 'qLastName',
  name: 'qFirstName',
  email: 'qEmail',
  password: 'qPassword',
  cycleLength: 'qCycleLength',
  funChallengePreview: 'qFunChallenge',
  healthIntro: 'qHealthIntro',
  healthDimensions: 'qHealthDimensions',
  whoSelfCheck: 'qHealthWhoCheck',
  healthDelivery: 'qHealthDelivery',
  healthIntensity: 'qHealthIntensity',
  healthSnapshot: 'qHealthSnapshot'
});

/** Season expected result for Specific (S) — stored on A historically, shown under S. */
export function resolveExpectedResultLabel(slots, coachAgreement) {
  const a = slots?.A || {};
  const L = a.labels || {};
  const revised = (coachAgreement?.revisedMilestones || [])
    .map((m) => m?.label)
    .filter(Boolean)
    .join(' · ');
  const miles = (a.milestones || [])
    .map((m) => m?.label)
    .filter(Boolean)
    .join(' · ');
  return (
    revised ||
    a.seasonQuest?.expectedResult?.label ||
    a.seasonQuest?.finalObjective?.label ||
    a.finalObjective?.label ||
    L.finalObjective ||
    L.milestones ||
    miles ||
    ''
  );
}

/**
 * Build snapshot rows for assessment Digiping card.
 * @param {object} slots
 * @param {object} status
 * @param {object} labelsByDim
 * @param {object} [coachAgreement]
 * @param {object} [copy] - optional i18n strings
 */
export function buildSmartSnapshot(slots, status, labelsByDim, coachAgreement, copy = {}) {
  const agreement = coachAgreement || emptyCoachAgreement();
  const pendingCoach = agreement.status === 'pending';
  const coachNote =
    copy.coachPendingNote ||
    'Awaiting coach agreement (milestones + relevance)';
  const coachMayRefine = copy.coachMayRefine || 'Coach may refine';
  const achievableSummary =
    copy.achievableSummary ||
    'Coach evaluates your training efforts against the expected results';
  const expectedResultLabel = resolveExpectedResultLabel(slots, agreement);

  return DIMENSIONS.map((dim) => {
    const meta = labelsByDim[dim] || { letter: dim, label: dim };
    const slot = slots[dim] || {};
    const L = slot.labels || {};
    let summary = '';
    let coachBadge = false;

    switch (dim) {
      case 'S':
        summary = [
          L.purpose,
          L.healthDimensions,
          L.healthSpecific,
          expectedResultLabel,
          L.competitionYears,
          L.style,
          L.primaryFocus,
          L.secondaryFocus,
          L.classement,
          L.rankingPoints ||
            (slot.rankingPoints != null ? `${slot.rankingPoints} pts` : null)
        ]
          .filter(Boolean)
          .join(' · ');
        break;
      case 'M': {
        summary = [L.whoSelfCheck, L.weeklyTraining, L.experience, L.level, slot.customLevel, L.metric]
          .filter(Boolean)
          .join(' · ');
        break;
      }
      case 'A': {
        // A = coach judgment of training effort vs expected results (not the result itself).
        summary = [achievableSummary, L.intensity, slot.resourceNote, L.grip, L.hand]
          .filter(Boolean)
          .join(' · ');
        if (pendingCoach) {
          coachBadge = true;
          summary = [summary, coachMayRefine].filter(Boolean).join(' · ');
        }
        break;
      }
      case 'R':
        summary = L.why || slot.whyItMatters || '';
        if (pendingCoach) {
          coachBadge = true;
          summary = [summary, coachNote].filter(Boolean).join(' · ');
        }
        break;
      case 'T':
        summary =
          L.cycle ||
          (slot.cycleWeeks
            ? `${slot.cycleWeeks}-week cycle` +
              (slot.reviewLastWeek !== false
                ? ' · last week: review & next plan'
                : '')
            : '');
        break;
      default:
        break;
    }

    return {
      dimension: dim,
      letter: meta.letter,
      label: meta.label,
      status: status[dim] || 'empty',
      summary: summary || '—',
      coachBadge
    };
  });
}
